import type { Spec } from 'vega'

import type { TerritoryPoint } from './api'

/**
 * Radar de relevância por pasta do Profiles: uma fatia por pasta, raio = peso.
 *
 * Vega puro, não Vega-Lite: o Vega-Lite não tem coordenada polar com rótulo fora da
 * fatia nem anéis-guia. Ajusta-se clicando ou arrastando dentro da fatia — a distância
 * ao centro vira o peso. Essa conta mora em `pickWedge`, em TypeScript, para ter teste;
 * o Vega só desenha.
 */

// Mais largo que alto: os rótulos das pastas saem para os lados.
export const RADAR_WIDTH = 700
export const RADAR_HEIGHT = 440
const CX = RADAR_WIDTH / 2
const CY = RADAR_HEIGHT / 2
export const INNER = 30
export const OUTER = 150
const STEP = 0.1

export interface RadarRow {
  index: number
  folder: string
  weight: number
  /** Territórios fora do descarte: só esses o peso move no ranking. */
  ranked: number
  total: number
}

export function radarRows(points: TerritoryPoint[], weights: Record<string, number>): RadarRow[] {
  const counts = new Map<string, { ranked: number; total: number }>()
  for (const point of points) {
    if (!point.folder) continue
    const entry = counts.get(point.folder) ?? { ranked: 0, total: 0 }
    entry.total += 1
    if (point.quadrant !== 'descarte') entry.ranked += 1
    counts.set(point.folder, entry)
  }
  return [...counts]
    .sort((a, b) => b[1].ranked - a[1].ranked || b[1].total - a[1].total || a[0].localeCompare(b[0]))
    .map(([folder, c], index) => ({ index, folder, weight: weights[folder] ?? 1, ...c }))
}

/** Coordenada no SVG → fatia e peso. 0 no topo, sentido horário, como o arco do Vega. */
export function pickWedge(x: number, y: number, n: number): { index: number; weight: number } | null {
  const dx = x - CX
  const dy = y - CY
  const r = Math.hypot(dx, dy)
  if (n === 0 || r > OUTER + 24) return null
  const angle = (Math.atan2(dx, -dy) + 2 * Math.PI) % (2 * Math.PI)
  const index = Math.min(n - 1, Math.floor(angle / ((2 * Math.PI) / n)))
  const raw = (r - INNER) / (OUTER - INNER)
  const weight = Math.round(Math.min(1, Math.max(0, raw)) / STEP) * STEP
  return { index, weight: Math.round(weight * 10) / 10 }
}

export function buildRadarSpec(n: number): Spec {
  const step = (2 * Math.PI) / Math.max(n, 1)
  const mid = `(datum.index + 0.5) * ${step}`
  const radius = `${INNER} + datum.weight * ${OUTER - INNER}`
  return {
    $schema: 'https://vega.github.io/schema/vega/v6.json',
    width: RADAR_WIDTH,
    height: RADAR_HEIGHT,
    autosize: 'none',
    data: [
      { name: 'folders', values: [] },
      { name: 'rings', values: [0.25, 0.5, 0.75, 1].map((w) => ({ w })) },
    ],
    marks: [
      {
        // Fatia cheia ao fundo: o teto de cada pasta, para o peso ler como fração.
        type: 'arc',
        from: { data: 'folders' },
        encode: {
          enter: {
            x: { value: CX },
            y: { value: CY },
            startAngle: { signal: `datum.index * ${step}` },
            endAngle: { signal: `(datum.index + 1) * ${step}` },
            innerRadius: { value: INNER },
            outerRadius: { value: OUTER },
            padAngle: { value: 0.015 },
            fill: { value: '#eef3f0' },
            cursor: { value: 'pointer' },
          },
        },
      },
      {
        type: 'symbol',
        from: { data: 'rings' },
        interactive: false,
        encode: {
          enter: {
            x: { value: CX },
            y: { value: CY },
            shape: { value: 'circle' },
            size: { signal: `PI * pow(${INNER} + datum.w * ${OUTER - INNER}, 2)` },
            fill: { value: 'transparent' },
            stroke: { signal: "datum.w === 1 ? '#8fa8a2' : '#c3d2cb'" },
            strokeDash: { signal: 'datum.w === 1 ? [] : [3, 4]' },
          },
        },
      },
      {
        type: 'arc',
        from: { data: 'folders' },
        interactive: false,
        encode: {
          update: {
            x: { value: CX },
            y: { value: CY },
            startAngle: { signal: `datum.index * ${step}` },
            endAngle: { signal: `(datum.index + 1) * ${step}` },
            innerRadius: { value: INNER },
            outerRadius: { signal: radius },
            padAngle: { value: 0.015 },
            // Pasta sem território no ranking: o peso não move nada, e a cor diz isso.
            fill: { signal: "datum.ranked > 0 ? '#c9373a' : '#c3d2cb'" },
            fillOpacity: { value: 0.85 },
            stroke: { value: '#fbfcfa' },
            strokeWidth: { value: 1.5 },
          },
        },
      },
      {
        type: 'text',
        from: { data: 'folders' },
        interactive: false,
        encode: {
          update: {
            x: { value: CX },
            y: { value: CY },
            theta: { signal: mid },
            radius: { signal: `max(${radius} - 12, ${INNER + 10})` },
            text: { signal: "format(datum.weight, '.1f')" },
            align: { value: 'center' },
            baseline: { value: 'middle' },
            font: { value: 'DM Mono, ui-monospace' },
            fontSize: { value: 10 },
            fill: { signal: "datum.ranked > 0 && datum.weight > 0.2 ? '#fbfcfa' : '#5e7573'" },
          },
        },
      },
      {
        type: 'text',
        from: { data: 'folders' },
        interactive: false,
        encode: {
          enter: {
            x: { value: CX },
            y: { value: CY },
            theta: { signal: mid },
            radius: { value: OUTER + 14 },
            text: { signal: "[datum.folder, datum.ranked + ' no ranking · ' + datum.total]" },
            align: { signal: `sin(${mid}) > 0.2 ? 'left' : sin(${mid}) < -0.2 ? 'right' : 'center'` },
            baseline: { signal: `cos(${mid}) > 0.5 ? 'bottom' : cos(${mid}) < -0.5 ? 'top' : 'middle'` },
            limit: { value: 175 },
            lineHeight: { value: 13 },
            font: { value: 'DM Sans, system-ui' },
            fontSize: { value: 11 },
            fill: { signal: "datum.ranked > 0 ? '#053b3e' : '#8fa8a2'" },
          },
        },
      },
    ],
  }
}
